import { clamp } from '../shared/scalar';
import { isBarTick, type Tempo } from '../shared/tempo';
import { hasStatus, isRooted, walkingSpeed } from './enemy-status';
import { moveUntilBlocked, type Obstacles, resolveObstacles } from './obstacles';
import type { SpatialHash } from './spatial-hash';
import type { EnemyState, MarkSource, PlayerId, PlayerState, SimEvent, SimState } from './state';

interface Point {
  x: number;
  y: number;
}

interface Circle extends Point {
  radius: number;
}

export function touches(body: Circle, center: Point, radius: number): boolean {
  const dx = body.x - center.x;
  const dy = body.y - center.y;
  const reach = radius + body.radius;
  return dx * dx + dy * dy <= reach * reach;
}

export function pushAway(body: Circle, from: Point, distance: number, obstacles: Obstacles): void {
  const dx = body.x - from.x;
  const dy = body.y - from.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) {
    moveUntilBlocked(body, distance, 0, obstacles);
    return;
  }
  moveUntilBlocked(body, (dx / length) * distance, (dy / length) * distance, obstacles);
}

// A boss always makes its way to the stage: effects may slow it, never push it back nor hold it.
export function knockBack(
  enemy: EnemyState,
  from: Point,
  distance: number,
  obstacles: Obstacles,
): void {
  if (!enemy.isBoss) {
    pushAway(enemy, from, distance, obstacles);
  }
}

export function markEnemy(
  state: SimState,
  enemy: EnemyState,
  source: MarkSource,
  damageMul: number,
  ticks: number,
): void {
  enemy.marks ??= {};
  const held = enemy.marks[source];
  enemy.marks[source] = {
    damageMul: Math.max(held?.damageMul ?? 1, damageMul),
    untilTick: Math.max(held?.untilTick ?? 0, state.tick + ticks),
  };
}

export function lapseMarks(enemy: EnemyState, tick: number): void {
  const { marks } = enemy;
  if (marks === undefined) {
    return;
  }
  if (marks.lure !== undefined && marks.lure.untilTick <= tick) {
    delete marks.lure;
  }
  if (marks.ribbon !== undefined && marks.ribbon.untilTick <= tick) {
    delete marks.ribbon;
  }
  if (marks.lure === undefined && marks.ribbon === undefined) {
    delete enemy.marks;
  }
}

function markDamageMul({ marks }: EnemyState): number {
  if (marks === undefined) {
    return 1;
  }
  return (marks.lure?.damageMul ?? 1) * (marks.ribbon?.damageMul ?? 1);
}

export function hurtEnemy(
  state: SimState,
  enemy: EnemyState,
  damage: number,
  by: PlayerId | null,
  from: Point | null,
): void {
  if (enemy.hp <= 0) {
    return;
  }
  const marked = damage * markDamageMul(enemy);
  const frontMul = from === null ? 1 : frontDamageMul(enemy, from);
  const armorMul = enemy.armor === undefined || enemy.armor.open ? 1 : enemy.armor.damageMul;
  const dealt = marked * frontMul * armorMul;
  enemy.hp -= dealt;
  if (by !== null) {
    enemy.lastHitBy = by;
  }
  state.stats.damageDealt += dealt;
  const hit: Extract<SimEvent, { type: 'enemyHit' }> = {
    type: 'enemyHit',
    id: enemy.id,
    damage: dealt,
    x: enemy.x,
    y: enemy.y,
  };
  if (frontMul !== 1) {
    hit.front = true;
  }
  if (armorMul !== 1) {
    hit.armored = true;
  }
  if (by !== null) {
    hit.by = by;
  }
  if (from !== null) {
    hit.fromX = from.x;
    hit.fromY = from.y;
  }
  state.events.push(hit);
}

function frontDamageMul(enemy: EnemyState, from: Point): number {
  const { front } = enemy;
  if (front === undefined) {
    return 1;
  }
  const facing = (from.x - enemy.x) * front.x + (from.y - enemy.y) * front.y;
  return facing > 0 ? front.damageMul : 1;
}

export function compound(factor: number, times: number): number {
  let value = 1;
  for (let i = 0; i < times; i++) {
    value *= factor;
  }
  return value;
}

export function wholeTicks(ticks: number): number {
  return Math.floor(ticks + 0.5);
}

export function keepWhere<T>(items: T[], keep: (item: T) => boolean): void {
  let kept = 0;
  for (const item of items) {
    if (keep(item)) {
      items[kept] = item;
      kept += 1;
    }
  }
  items.length = kept;
}

// The grid holds the bad vibes where they stand. The strongest slow wins, in any order.
export function slowEnemies(
  state: SimState,
  grid: SpatialHash,
  at: Point,
  radius: number,
  slowFactor: number,
): boolean {
  let reached = false;
  const found = grid.query(at.x, at.y, radius);
  for (let i = 0; i < found; i++) {
    const enemy = state.enemies[grid.result(i)];
    if (enemy !== undefined && enemy.hp > 0) {
      enemy.slowFactor = Math.min(enemy.slowFactor, slowFactor);
      reached = true;
    }
  }
  return reached;
}

export function healPlayer(state: SimState, player: PlayerState, amount: number): void {
  const healed = Math.min(player.maxHp - player.hp, amount);
  if (healed <= 0) {
    return;
  }
  player.hp += healed;
  state.events.push({ type: 'playerHealed', playerId: player.id, amount: healed });
}

export function healPlayersOnBar(
  state: SimState,
  tempo: Tempo,
  at: Point,
  radius: number,
  amount: number,
): void {
  if (!isBarTick(state.tick, tempo)) {
    return;
  }
  for (const player of state.players) {
    if (!player.downed && touches(player, at, radius)) {
      healPlayer(state, player, amount);
    }
  }
}

// The grid holds the bad vibes where they stand, and is rebuilt where the wave knocked them.
export function shockwave(
  state: SimState,
  grid: SpatialHash,
  at: Point,
  radius: number,
  damage: number,
  knockback: number,
  by: PlayerId | null,
  obstacles: Obstacles,
): boolean {
  let reached = false;
  const found = grid.queryInListOrder(at.x, at.y, radius);
  for (let i = 0; i < found; i++) {
    const enemy = state.enemies[grid.result(i)];
    if (enemy !== undefined && enemy.hp > 0) {
      hurtEnemy(state, enemy, damage, by, at);
      knockBack(enemy, at, knockback, obstacles);
      reached = true;
    }
  }
  if (reached) {
    grid.rebuild(state.enemies);
  }
  return reached;
}

// Replaces the step the steering just gave: same speed, slows, statuses and obstacles, new goal.
export function drawTo(enemy: EnemyState, at: Point, contact: number, obstacles: Obstacles): void {
  if (enemy.isBoss || hasStatus(enemy, 'bubbled')) {
    return;
  }
  const step = isRooted(enemy) ? 0 : walkingSpeed(enemy);
  const dx = at.x - enemy.prevX;
  const dy = at.y - enemy.prevY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const travel = clamp(distance - contact, 0, step);
  enemy.x = distance === 0 ? enemy.prevX : enemy.prevX + (dx / distance) * travel;
  enemy.y = distance === 0 ? enemy.prevY : enemy.prevY + (dy / distance) * travel;
  resolveObstacles(enemy, enemy.prevX, enemy.prevY, obstacles);
}
